// Evaluation API client — thin wrapper over the shared authed axios instance.
// Talks to the backend eval REST surface (POST /runs to trigger, GET /runs/{id}
// to poll). Auth header + base URL + token refresh are handled by `api`.

import { api } from './api';

export interface EvalRunCreated {
  run_id: string;
  run_name: string;
  status: string;
}

export interface MetricSummary {
  average: number;
  passed: number;
  total: number;
}

export interface EvalRunResult {
  dataset_name?: string;
  run_name?: string;
  total_items?: number;
  successful?: number;
  failed?: number;
  average_scores?: Record<string, number>;
  metric_summaries?: Record<string, MetricSummary>;
  started_at?: string;
  completed_at?: string;
}

export interface EvalRunStatus {
  run_id: string;
  status: string; // PENDING | RUNNING | COMPLETED | FAILED
  run_name?: string;
  dataset_name?: string;
  metrics?: string[] | null;
  max_items?: number | null;
  mode?: string | null;
  created_at?: string;
  started_at?: string | null;
  completed_at?: string | null;
  result?: EvalRunResult | null;
  error?: string | null;
}

export interface CreateEvalRunBody {
  dataset_name: string;
  run_name?: string;
  metrics?: string[];
  max_items?: number;
  mode?: string;
}

export interface EvalRunListResponse {
  runs: EvalRunStatus[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export const evaluationApi = {
  createRun: async (body: CreateEvalRunBody): Promise<EvalRunCreated> => {
    const res = await api.post<EvalRunCreated>('/api/v1/eval/runs', body);
    const data = res?.data as EvalRunCreated | null;
    if (!data || !data.run_id) {
      // A 2xx with an empty/null body almost always means the request didn't
      // reach the eval endpoint (stale backend without the eval router, or a
      // wrong API base URL). Fail loudly instead of crashing on `null.run_id`.
      throw new Error(
        `Unexpected empty response from POST /api/v1/eval/runs (HTTP ${res?.status ?? '?'}). ` +
          "If status is 200 with no body, the path may be blocked by the frontend API " +
          "whitelist (apiWhitelist.ts) — ensure '/api/v1/eval/' is listed. Otherwise check " +
          'VITE_API_BASE_URL points at the backend.',
      );
    }
    return data;
  },
  getRun: async (runId: string): Promise<EvalRunStatus> => {
    const res = await api.get<EvalRunStatus>(`/api/v1/eval/runs/${runId}`);
    if (!res?.data) {
      throw new Error(`Empty response from GET /api/v1/eval/runs/${runId} (HTTP ${res?.status ?? '?'})`);
    }
    return res.data;
  },
  listRuns: async (limit: number, offset: number): Promise<EvalRunListResponse> => {
    const res = await api.get<EvalRunListResponse>('/api/v1/eval/runs', {
      params: { limit, offset },
    });
    return res?.data ?? { runs: [], total: 0, limit, offset, has_more: false };
  },
};
